import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { X, Plus, ChevronRight, LogOut, User, Settings, Bookmark } from 'lucide-react';
import { buildApiUrl } from '../config/apiConfig';

const MobileDrawer = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const drawerRef = useRef(null);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch topics
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        setLoading(true);
        const res = await fetch(buildApiUrl('/topics'));
        if (res.ok) {
          const data = await res.json();
          // Get top 6 topics (featured first, then by popularity)
          setTopics(data.slice(0, 6));
        } else {
          console.error('Failed to fetch topics:', res.status);
        }
      } catch (err) {
        console.error('Failed to fetch topics:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && topics.length === 0) {
      fetchTopics();
    } else if (isOpen && topics.length > 0) {
      setLoading(false);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleLinkClick = () => {
    onClose();
  };

  const handleLogout = () => {
    logout();
    onClose();
    navigate('/login');
  };

  const handleAskQuestion = () => {
    if (user) {
      navigate('/ask');
    } else {
      navigate('/signup');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-200 md:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="fixed top-0 left-0 h-full w-[300px] bg-white shadow-2xl z-50 transform transition-transform duration-200 ease-out md:hidden overflow-y-auto"
        role="dialog"
        aria-label="Navigation menu"
      >
        {/* Close Button */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <span className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Menu
          </span>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Ask Question CTA */}
        <div className="p-4 border-b border-gray-100">
          <button
            onClick={handleAskQuestion}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all font-medium active:scale-[0.98]"
          >
            <Plus className="w-5 h-5" />
            Ask Question
          </button>
        </div>

        {/* Topics Section */}
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Browse Topics
          </h3>

          {loading ? (
            // Loading skeleton
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-10 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              {/* All Topics Link - First and Prominent */}
              <Link
                to="/topics"
                onClick={handleLinkClick}
                className="flex items-center justify-between px-4 py-3 text-sm font-semibold text-purple-600 hover:bg-purple-50 rounded-lg transition-all border border-purple-200 hover:border-purple-300 hover:shadow-sm group mb-2"
              >
                <span>All Topics</span>
                <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </Link>

              {/* Individual Topic Links */}
              {topics.map(topic => (
                <Link
                  key={topic.id}
                  to={`/topics/${topic.slug}`}
                  onClick={handleLinkClick}
                  className="flex items-center justify-between px-3 py-2.5 hover:bg-purple-50 rounded-lg transition-all group active:scale-[0.98]"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: topic.color || '#8B5CF6' }}
                    />
                    <span className="text-sm font-medium text-gray-700 group-hover:text-purple-600 truncate">
                      {topic.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xs text-gray-500">
                      {topic.question_count}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-purple-600" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Public Pages Section */}
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Explore
          </h3>
          <div className="space-y-1">
            <Link
              to="/leaderboard"
              onClick={handleLinkClick}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-purple-50 rounded-lg transition-all text-gray-700 active:scale-[0.98]"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              <span className="font-medium">Leaderboard</span>
            </Link>
          </div>
        </div>

        {/* Divider */}
        {user && (
          <div className="border-t border-gray-200"></div>
        )}

        {/* User Menu (Only for logged-in users) */}
        {user && (
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Your Account
            </h3>
            <div className="space-y-1">
              <Link
                to="/saved"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-purple-50 rounded-lg transition-all text-gray-700 active:scale-[0.98]"
              >
                <Bookmark className="w-5 h-5 text-gray-500" />
                <span className="font-medium">Saved</span>
              </Link>

              <Link
                to={`/profile/${user?.username}`}
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-purple-50 rounded-lg transition-all text-gray-700 active:scale-[0.98]"
              >
                <User className="w-5 h-5 text-gray-500" />
                <span className="font-medium">Profile</span>
              </Link>

              <Link
                to={`/profile/${user?.username}?tab=settings`}
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-purple-50 rounded-lg transition-all text-gray-700 active:scale-[0.98]"
              >
                <Settings className="w-5 h-5 text-gray-500" />
                <span className="font-medium">Settings</span>
              </Link>

              {/* Admin Panel Link - Only for Admins */}
              {user?.is_admin && (
                <>
                  <div className="border-t border-gray-200 my-2"></div>
                  <Link
                    to="/admin"
                    onClick={handleLinkClick}
                    className="flex items-center gap-3 px-3 py-2.5 bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 rounded-lg transition-all text-purple-700 font-semibold active:scale-[0.98] border border-purple-200"
                  >
                    <Settings className="w-5 h-5 text-purple-600" />
                    <span>Admin Panel</span>
                    <svg className="w-4 h-4 ml-auto text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </Link>
                </>
              )}

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-red-50 rounded-lg transition-all text-red-600 active:scale-[0.98]"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MobileDrawer;
